// CLI v0.8 — `grid env get <key> [<name>]`. Spec v17 §13.8.
//
// The server has no key-scoped endpoint; we list the full set and filter
// client-side. Acceptable for env (always small) but a real concern for
// secrets — see commands/secrets/get.ts which does NOT pull values.

import { Command } from 'commander';
import { Command as CommanderCommand } from 'commander';
import { getV2Client, type IV2Client } from '../../api/v2-client.js';
import { handleV2Error } from '../../handle-error.js';
import { V2ApiError } from '../../api/v2-errors.js';
import { resolveEntityTarget } from '../../services/resolve-entity-target.js';
import { Output } from '../../output.js';
import { addOutputOption, isStructuredOutput } from '../../services/output-format.js';

interface EnvListResponse { env: Array<{ key: string; value: string }> }

export interface RunEnvGetOpts {
  client: IV2Client;
  name: string | undefined;
  key: string;
  cwd?: string;
  json?: boolean;
  output?: string;
}

export async function runEnvGet(opts: RunEnvGetOpts): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });
  const target = await resolveEntityTarget(opts.client, opts.name, opts.cwd);
  const resp = await opts.client.get<EnvListResponse>(
    `/api/v2/runtimes/${encodeURIComponent(target.entity_id)}/env`,
  );
  const found = resp.env.find((e) => e.key === opts.key);
  if (!found) {
    throw new V2ApiError(
      404,
      'KEY_NOT_FOUND',
      `Env key '${opts.key}' is not set on ${target.name}.`,
    );
  }
  out.json(() => ({ key: found.key, value: found.value }));
  // Human mode keeps the bare value on its own line so `$(grid env get KEY)`
  // captures exactly the value with no envelope.
  out.human(() => found.value);
}

export const envGetCommand: CommanderCommand = addOutputOption(
  new Command('get')
    .description('Print the value of one env var')
    .argument('<key>', 'Env var key')
    .argument('[name]', 'Entity slug or id (defaults to the entity linked in this directory)')
    .option('--json', 'Machine-readable JSON output (alias of -o json)'),
).action(async (key: string, name: string | undefined, opts: { json?: boolean; output?: string }) => {
  try {
    const client = await getV2Client();
    await runEnvGet({ client, name, key, json: opts.json, output: opts.output });
  } catch (err) {
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
});
